import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray } from "react-hook-form";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

// APIs
import { GetDepartments } from "../../../api/masters/departmentAPI";
import { BulkImportStaff, StoreStaff } from "../../../api/masters/staffAPI";
import BulkImport from "../../../components/BulkImport";
import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";

// ✅ Validation Schema (aligned with your Mongoose model)
const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  departmentId: yup.string().optional(),
  nature: yup.string().required("Nature is required"),
  address: yup.string().optional(),
  mobileNo: yup.string(),
  phoneNo: yup.string().optional()
});

const AddStaff = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [departments, setDepartments] = useState([]);

  const { register, control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });

  // Fetch dropdown data
  const fetchDepartments = async () => {
    try {
      const res = await GetDepartments();
      if (res.success) setDepartments(res.data);
      else showToast("error", res.message);
    } catch (err) {
      showToast("error", err.message);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        dispatch({ type: "LOADER_START" });

        await fetchDepartments();
      } catch (error) {
        showToast("error", error);
      } finally {
        dispatch({ type: "LOADER_STOP" });
      }
    };

    loadData();
  }, []);

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });
      const res = await StoreStaff(data);
      if (res.success) {
        showToast("success", res.message);
        navigate(`/masters/staff/list`);
      } else {
        showToast("error", res.message);
      }
    } catch (err) {
      showToast("error", err.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
  useKeyboardSubmit(handleSubmit, onSubmit);
  useEnterNavigation(formRef);

  const bulkColumns = [
    { key: "name", label: "Staff Name", sample: "Staff Name" },
    { key: "department", label: "Department", sample: "Department" },
    { key: "nature", label: "Nature", sample: "Nature" },
    { key: "address", label: "Address", sample: "Address" },
    { key: "mobileNo", label: "Mobile No.", sample: "Mobile No." },
    { key: "phoneNo", label: "Phone No.", sample: "Phone No." }
  ];

  const handleBulkImport = async payload => {
    const response = await BulkImportStaff(payload);

    if (response.success) {
      showToast("success", response.message);
      navigate("/masters/staff/list");
    } else {
      showToast("error", response.message);
    }
  };

  return (
    <Fragment>
      <Breadcrumb parent="Staff" title="Add Staff" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Add Staff</h5>
                <Link to={`/masters/staff/list`}>
                  <button className="btn btn-primary" id="list">
                    List Staff
                  </button>
                  <UncontrolledTooltip placement="top" target="list">
                    List Staff
                  </UncontrolledTooltip>
                </Link>
              </div>

              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label>Name *</label>
                      <input
                        className="form-control"
                        placeholder="Staff Name"
                        {...register("name")}
                      />
                      {errors.name &&
                        <p className="text-danger">
                          {errors.name.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Department</label>
                      <select
                        className="form-control"
                        {...register("departmentId")}
                      >
                        <option value="">Select Department</option>
                        {departments.map(dep =>
                          <option key={dep._id} value={dep._id}>
                            {dep.name}
                          </option>
                        )}
                      </select>
                      {errors.departmentId &&
                        <p className="text-danger">
                          {errors.departmentId.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Nature *</label>
                      <select
                        className="form-control"
                        placeholder="Nature"
                        {...register("nature")}
                      >
                        <option value="">Select Nature</option>
                        <option value="OT Technician">OT Technician</option>
                        <option value="OT Assistant">OT Assistant</option>
                        <option value="Others">Others</option>
                      </select>
                      {errors.nature &&
                        <p className="text-danger">
                          {errors.nature.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Address</label>
                      <textarea
                        className="form-control"
                        placeholder="Address"
                        {...register("address")}
                      />
                      {errors.address &&
                        <p className="text-danger">
                          {errors.address.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Mobile No</label>
                      <input
                        className="form-control"
                        {...register("mobileNo")}
                      />
                      {errors.mobileNo &&
                        <p className="text-danger">
                          {errors.mobileNo.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Phone No</label>
                      <input
                        className="form-control"
                        {...register("phoneNo")}
                      />
                      {errors.phoneNo &&
                        <p className="text-danger">
                          {errors.phoneNo.message}
                        </p>}
                    </div>
                  </div>
                </div>

                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
            <BulkImport
              columns={bulkColumns}
              onSubmit={handleBulkImport}
              payloadKey="staffs"
              sampleFileName="staff_sample.csv"
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddStaff;
